/**
 * 🎯 LA PILE DU CHATTEUR — ce qui lui est CONFIÉ, et à personne d'autre.
 *
 * Kayla, 09-09-2026 : « on ne choisit plus sa discussion, on clique Suivant ».
 * La file courte du 08-09 classait ce que TOUT LE MONDE voyait : deux
 * chatteurs pouvaient ouvrir le même fan. La pile ATTRIBUE — le serveur tient
 * « jamais deux » dans sa base, et l'écran ne fait que rendre ce qu'il a reçu.
 *
 * ⛔ Aucune règle ici. Le rang, la raison, l'ordre, le délai, la libération :
 * tout vient de `GET /api/conversations/pile`. Ce module ne fait que MARIER
 * la pile (l'ordre et l'attribution) aux cartes déjà chargées par la liste
 * (le contenu) — les deux formes n'ont ni les mêmes champs ni la même route.
 * Recopier ici un seuil du serveur, c'est le voir diverger au premier
 * correctif.
 *
 * Fonctions pures, testées sans DOM (`__tests__/pileDuChatteur.test.ts`).
 */

import { atom } from "jotai";

import type { TonBadge } from "@/shared/ui";

import type { LockStatus, LockView } from "./lockLogic";
import {
  formatRevenue,
  type MockConversation,
  type MomentAReprendre,
} from "./mockData";

/** Un fan tel que la pile le nomme — juste de quoi titrer un onglet. */
export interface FanDeLaPile {
  username?: string | null;
  first_name?: string | null;
}

/** Une ligne de `GET /api/conversations/pile`. */
export interface ItemDePile {
  conversation_id: string;
  creator_id: string | null;
  fan: FanDeLaPile;
  /** `signaux_de_file.ORDRE` — payé, prix, nouveau, bot, attention, relance,
   *  offre, sans réponse. Rendu tel quel : l'écran n'en juge pas. */
  signal: string;
  signal_le: string;
  priorite_rang: number;
  priorite_raison: string;
  attribuee_le: string;
  /** `null` = elle me reste tant que je suis là (vague d'une reprise). */
  a_repondre_avant: string | null;
  /** 📌 Je me la suis RÉSERVÉE : personne d'autre ne l'a tant que je suis
   *  présent, et rien ne me la reprend — ni « il a répondu », ni le délai.
   *  ⛔ Rien à voir avec le tag « PIN » du triage, qui est celui de l'équipe.
   *  Optionnel : absent tant que Cyrano n'a pas déployé le champ (le cockpit
   *  part toujours en premier). Absent ⇒ `false`, donc l'écran d'avant. */
  epinglee?: boolean;
}

export interface ReponsePile {
  pile: ItemDePile[];
  presents: number;
  battement_secondes: number;
  /** 📌 Combien j'en ai réservé, et combien j'ai le droit d'en réserver — le
   *  « 2/5 » du menu. Le plafond vient du SERVEUR (`PILE_EPINGLES_MAX`) :
   *  l'écran ne porte pas le chiffre, il l'affiche. Absents tant que Cyrano
   *  n'a pas déployé — cf. `EPINGLES_MAX_PAR_DEFAUT`. */
  epingles?: number;
  epingles_max?: number;
  /** 🎯 Combien ma file peut porter AU TOTAL (`PILE_PLAFOND`) — le « 7/10 »
   *  du poste, et ce qui ferme « Ajouter » quand elle est pleine. Absent tant
   *  que Cyrano n'a pas déployé — cf. `PLAFOND_PAR_DEFAUT`. */
  plafond?: number;
}

/** Ce que rendent `pile/epingler` et `pile/desepingler`. */
export interface ReponseEpingle {
  epinglee: boolean;
  epingles: number;
  epingles_max: number;
}

/**
 * 📌 Le plafond à afficher tant que le serveur ne l'a pas dit.
 *
 * ⛔ Ce n'est PAS la règle — c'est le serveur qui refuse la seizième, et lui
 * seul. Ce chiffre ne sert qu'au libellé « 2/15 » pendant la fenêtre où le
 * cockpit est déployé et Cyrano pas encore (l'ordre est toujours celui-là).
 * Cinq le 10-09-2026, puis égal au plafond de la file depuis le 13-09 : tout
 * ce qu'un chatteur ajoute à sa file à la main est une épingle.
 */
export const EPINGLES_MAX_PAR_DEFAUT = 15;

/**
 * 🎯 Le plafond de la file à afficher tant que le serveur ne l'a pas dit —
 * même rôle, même réserve que `EPINGLES_MAX_PAR_DEFAUT` : c'est le serveur
 * qui refuse la seizième (`pile_pleine`), jamais cet écran. Quinze est le
 * `PILE_PLAFOND` par défaut de Cyrano depuis le 14-09-2026 (Kayla :
 * « j'aimerais augmenter la file à 15 ») — dix du 10 au 14-09.
 */
export const PLAFOND_PAR_DEFAUT = 15;

export interface ReponseSuivant {
  conversation: ItemDePile | null;
  pile: number;
  battement_secondes: number;
}

/**
 * Une ligne de `GET /api/conversations/recommandations` — une discussion qui
 * porte un signal et n'est à PERSONNE.
 *
 * ⛔ Elle n'a ni `attribuee_le` ni `a_repondre_avant`, et ce n'est pas un
 * oubli : elle n'a été confiée à personne. Le serveur ne les invente pas, on
 * ne les invente pas non plus — `itemDeLaRecommandation` les rend vides, comme
 * `itemDeLaCarte` le fait déjà pour une carte hors pile.
 */
export interface ItemRecommande {
  conversation_id: string;
  creator_id: string | null;
  fan: FanDeLaPile;
  signal: string;
  signal_le: string;
  priorite_rang: number;
  priorite_raison: string;
}

export interface ReponseRecommandations {
  recommandations: ItemRecommande[];
  /** Combien il y en a EN TOUT — la liste est plafonnée côté serveur. */
  total: number;
  /** 💶 Combien, parmi TOUTES, portent un prix dans l'air — le fan l'a demandé
   *  ou nous l'avons annoncé (`signaux_de_file.SIGNAUX_PRIX_DANS_L_AIR`).
   *  Absent sur un backend d'avant le 16-09 : le cockpit part avant Cyrano. */
  prix_dans_l_air?: number;
}

export interface ReponseLiberer {
  libere: boolean;
  pile: number;
}

export interface ReponseRemplacer {
  conversation: ItemDePile | null;
  pile: number;
}

/** La recommandation, dans la forme que la ligne sait rendre. */
export function itemDeLaRecommandation(r: ItemRecommande): ItemDePile {
  return {
    conversation_id: r.conversation_id,
    creator_id: r.creator_id,
    fan: r.fan,
    signal: r.signal,
    signal_le: r.signal_le,
    priorite_rang: r.priorite_rang,
    priorite_raison: r.priorite_raison,
    attribuee_le: "",
    a_repondre_avant: null,
    epinglee: false,
  };
}

/**
 * 📋 Les conversations de MA pile, telles que le dernier battement les a
 * rendues — pour que la porte (`reservation`, via `useOpenFanTab`) sache ce
 * qui est à moi avant que la liste l'ait appris. Écrit par
 * `usePileDuChatteur` à chaque battement, vidé quand je quitte. Même patron
 * que `regieFocusConversationIdsAtom` (`lockLogic.ts`) : module-level,
 * défaut vide, et un caller qui l'ignore garde l'écran d'avant.
 */
export const maFileAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Le serveur en accepte 50 par appel (`GET /api/conversations?ids=`). */
export const MAX_IDS_HYDRATATION = 50;

/**
 * Le nom à écrire sur l'onglet quand la carte n'est pas encore chargée.
 * Chaîne vide plutôt qu'un « Fan inconnu » inventé : l'onglet prendra le vrai
 * nom dès que la carte arrive.
 */
export function nomDuFan(item: ItemDePile): string {
  const u = (item.fan?.username ?? "").trim();
  if (u) return u;
  return (item.fan?.first_name ?? "").trim();
}

/**
 * Les conversations de la tête dont on n'a pas la carte — à hydrater par
 * `GET /api/conversations?ids=…`.
 *
 * La pile d'une reprise de shift porte tout ce qui a attendu la nuit ; la
 * page chargée par la liste n'en contient qu'une partie. On ne rattrape que
 * ce qui est AFFICHÉ, jamais la pile entière : sinon une reprise à 94
 * conversations tirerait 94 cartes que personne ne regarde.
 */
export function idsAHydrater(
  tete: readonly ItemDePile[],
  cartesConnues: ReadonlySet<string>,
): string[] {
  const manquants: string[] = [];
  for (const item of tete) {
    if (cartesConnues.has(item.conversation_id)) continue;
    if (manquants.includes(item.conversation_id)) continue;
    manquants.push(item.conversation_id);
    if (manquants.length >= MAX_IDS_HYDRATATION) break;
  }
  return manquants;
}

/**
 * Les cartes à afficher : celles de la tête de pile, DANS L'ORDRE DE LA PILE,
 * portant le rang et la raison que le serveur leur a donnés.
 *
 * 🪤 Le rang et la raison viennent de l'ITEM, pas de la carte. La carte les
 * porte aussi (`sort=priorite`, trois rangs, livré le 08-09) et ce n'est PAS
 * la même échelle que les huit rangs de la pile : garder ceux de la carte
 * afficherait « offre 50 € » sur une conversation que la pile a confiée pour
 * une tout autre raison.
 *
 * Une carte encore absente est simplement sautée — elle apparaîtra au tour
 * suivant, une fois hydratée. On ne fabrique pas de carte creuse : une carte
 * sans nom ni dernier message est pire qu'une case vide.
 */
export function cartesDeLaPile(
  tete: readonly ItemDePile[],
  cartes: readonly MockConversation[],
): MockConversation[] {
  const parId = new Map<string, MockConversation>();
  for (const c of cartes) parId.set(c.id, c);
  const retenues: MockConversation[] = [];
  for (const item of tete) {
    const carte = parId.get(item.conversation_id);
    if (!carte) continue;
    retenues.push({
      ...carte,
      priorite_rang: item.priorite_rang,
      priorite_raison: item.priorite_raison,
    });
  }
  return retenues;
}

/**
 * Cette conversation a dépassé le délai que le serveur lui avait donné.
 *
 * Sert à la DIRE, pas à agir : c'est le serveur qui libère, à sa prochaine
 * réconciliation. `a_repondre_avant` nul = aucune course (vague de reprise),
 * donc jamais en retard.
 */
export function estEnRetard(item: ItemDePile, maintenant: number): boolean {
  if (!item.a_repondre_avant) return false;
  const limite = Date.parse(item.a_repondre_avant);
  return Number.isFinite(limite) && maintenant > limite;
}

/**
 * La conversation vers laquelle « Suivant » emmène : la tête de ma pile, en
 * sautant celle que je quitte.
 *
 * Le serveur rend déjà ce choix (`POST /api/conversations/suivant`) ; cette
 * fonction sert à l'écran AVANT sa réponse (l'ordre est le même, et le clic
 * ne doit pas attendre un aller-retour pour surligner la suivante).
 */
export function laSuivante(
  pile: readonly ItemDePile[],
  quitte: string | null,
): ItemDePile | null {
  for (const item of pile) {
    if (quitte && item.conversation_id === quitte) continue;
    return item;
  }
  return null;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* LA LIGNE — ce que le chatteur lit d'un coup d'œil (Kayla, 09-09-2026)       */
/* ────────────────────────────────────────────────────────────────────────── */
/*
 * « À gauche, au lieu d'avoir plein de discussions comme avant, il n'y a que
 * juste une ligne. C'est plus facile et l'ordre est bien fait. » Le modèle est
 * Fanvue : l'avatar, le prénom, POURQUOI en un mot avec sa couleur, depuis
 * quand le fan attend, et l'argent en jeu s'il y en a.
 *
 * ⛔ Le mot et la couleur sont indexés par le NOM du signal que le serveur
 * rend (`signaux_de_file.ORDRE`). Un signal que cette table ne connaît pas se
 * rend tel quel, en gris : l'écran n'en juge pas, et le jour où le serveur en
 * ajoute un, la ligne le montre au lieu de le cacher.
 */

/** Le mot, un par signal, en français — la clé i18n porte le nom du signal. */
const MOT_DU_SIGNAL: Record<string, string> = {
  paye: "payé",
  demande_de_prix: "prix",
  // 💶 NOUS avons dit que c'est payant — l'étape cochée du flow, ou 2.0 qui
  // s'est coupé dessus. Kayla, 16-09 : « on a annoncé le prix, donc c'est
  // terminé, même s'il reste encore une étape du flow ».
  prix_annonce: "prix annoncé",
  nouveau: "nouveau",
  accusation_bot: "bot",
  attention_haute: "chaud",
  relance_apres_offre: "relance",
  offre_en_attente: "offre",
  sans_reponse: "attend",
  // Le fond de la file (Kayla, 09-09 au soir) : un humain a écrit, le fan se
  // tait depuis un jour. Gris, comme l'attente : ce n'est pas de l'argent.
  relance: "à relancer",
  // 🎯 Le dixième rang (Kayla, 10-09) : « la plupart n'ont pas de pastille,
  // elles ne sont pas étiquetées — elles devraient toutes l'être ». Mesuré le
  // même jour : 222 des 259 discussions du poste (86 %) n'en portaient aucune,
  // et les 222 étaient le même cas — on a répondu, le fan se tait. Gris, et au
  // fond : ce n'est pas du travail à faire, c'est un état.
  // ⛔ Il n'entre dans AUCUNE file (`estDansLaFile`) : c'est le serveur qui le
  //    dit (`SIGNAUX_HORS_FILE`), et le redire ici est le prix à payer pour
  //    que la file de repli, qui se calcule sans lui, ne les avale pas toutes.
  en_attente_du_fan: "répondu",
};

/**
 * 📌 Les discussions que le chatteur TIENT — un onglet fan ouvert — passent
 * devant, dans leur ordre ; le reste suit, dans le sien. Kayla, 09-09-2026 :
 * « les discussions sur lesquelles ils ont sélectionné actuellement devraient
 * être en haut dans la liste, comme si c'était des épinglés ». Rien d'autre
 * ne bouge : l'ordre du serveur reste l'ordre du serveur.
 */
export function ouvertesDabord<T>(
  liste: readonly T[],
  ouvertes: ReadonlySet<string>,
  idDe: (t: T) => string,
): T[] {
  if (ouvertes.size === 0) return [...liste];
  const devant: T[] = [];
  const derriere: T[] = [];
  for (const t of liste) (ouvertes.has(idDe(t)) ? devant : derriere).push(t);
  return [...devant, ...derriere];
}

/**
 * 📌 LES RÉSERVÉES EN TÊTE — et devant les ouvertes.
 *
 * Kayla, 10-09-2026 : « ils peuvent épingler cinq discussions max ». Une
 * épinglée n'est pas « plus urgente » qu'une autre : elle est À LUI, et c'est
 * ce qu'un épinglé veut dire. Le serveur les classe déjà ainsi ; cette
 * fonction rejoue le même ordre APRÈS `ouvertesDabord`, qui remonterait sinon
 * un onglet ouvert au-dessus d'une réservée.
 *
 * Stable : à l'intérieur de chaque groupe, l'ordre reçu est conservé — donc
 * les ouvertes restent devant parmi les réservées comme parmi les autres.
 */
export function epingleesDabord(liste: readonly ItemDePile[]): ItemDePile[] {
  const devant: ItemDePile[] = [];
  const derriere: ItemDePile[] = [];
  for (const item of liste) (item.epinglee ? devant : derriere).push(item);
  return devant.length === 0 ? [...liste] : [...devant, ...derriere];
}

/**
 * ⭐ À QUI EST CETTE DISCUSSION — l'étoile de la ligne, et la porte.
 *
 * Kayla, 11-09-2026 : « le chatter qui l'épingle, ça devient sienne. Dans les
 * discussions, on voit les petites étoiles à côté du pseudo. Quand c'est son
 * shift, les gens ne peuvent pas y aller — sauf s'il n'est pas présent dans le
 * shift : là, les autres chatteurs peuvent y aller. »
 *
 * 📋 Et le 12-09-2026, une chatteuse trouvant un collègue dans une discussion
 * de SA file : « quand c'est dans la file de quelqu'un, un autre chatter ne
 * doit pas pouvoir y aller ». Le 11-09 avait retiré le masquage (« je veux
 * les réafficher : juste qu'ils voient qui y a dans la conversation ») sans
 * poser la porte à sa place : seules l'épingle et l'onglet ouvert fermaient.
 * La porte est donc l'ATTRIBUTION — la pile qui la porte en ce moment
 * (`tenue_par`), épinglée ou non. L'épingle ne fait que la garder à lui
 * hors tour de rôle ; c'est l'attribution qui dit « il la tient ».
 *
 * Tout vient de la carte, telle que le serveur la sert (`epinglee_par`,
 * `epingle_protegee`, `tenue_par`) — sauf ce que MA pile sait avant la carte
 * (le cockpit part toujours en premier, et la liste bat moins souvent que la
 * pile) : `mesEpingles` et `maFile` complètent, ils ne contredisent jamais.
 * 🪤 `maFile` n'est pas décoratif : la liste se rafraîchit à la minute, la
 * pile toutes les trente secondes. Sans lui, une discussion qui vient de
 * passer de sa file à la mienne porterait encore SON identifiant sur ma
 * carte, et ma propre file me refuserait l'entrée.
 *
 *   · `parMoi`   — l'étoile pleine, sans nom : c'est à moi ;
 *   · `par`      — l'étoile et le nom d'un autre. Elle reste quand il n'est
 *                  pas là : la discussion est toujours la sienne, elle est
 *                  seulement OUVERTE aux autres en son absence ;
 *   · `tenuePar` — qui la tient EN CE MOMENT (l'attribution ouverte), moi
 *                  compris ; `null` = personne ;
 *   · `fermee`   — un autre la tient ET je ne suis pas superviseur : la ligne
 *                  ne s'ouvre pas, comme sous un cadenas. Les superviseurs
 *                  entrent partout.
 *
 * Rien n'est calculé sur une présence ici : « il la tient » est ce que le
 * serveur dit (`tenue_par`, et `epingle_protegee` tant que Cyrano ne sert
 * pas encore le premier), et lui le lit sur l'attribution ouverte de la
 * pile — la définition que la réconciliation applique déjà.
 */
export interface Reservation {
  /** `workos_user_id` de celui qui se l'est réservée ; `null` = personne. */
  par: string | null;
  parMoi: boolean;
  /** L'épingleur la tient en ce moment (présent, pas en pause). */
  tenue: boolean;
  /** 📋 `workos_user_id` de celui dont la pile la porte en ce moment, moi
   *  compris ; `null` = personne ne la tient. */
  tenuePar: string | null;
  /** Je ne peux pas l'ouvrir : un autre la tient, et je ne suis pas
   *  superviseur. */
  fermee: boolean;
}

export const AUCUNE_RESERVATION: Reservation = {
  par: null,
  parMoi: false,
  tenue: false,
  tenuePar: null,
  fermee: false,
};

const AUCUNE: ReadonlySet<string> = new Set();

export function reservation(
  carte: Pick<MockConversation, "id" | "epinglee_par" | "epingle_protegee" | "tenue_par">,
  moi: string | null,
  estSuperviseur: boolean,
  mesEpingles: ReadonlySet<string>,
  maFile: ReadonlySet<string> = AUCUNE,
): Reservation {
  const parMoi = mesEpingles.has(carte.id) || (!!moi && carte.epinglee_par === moi);
  const par = carte.epinglee_par ?? (parMoi ? moi : null);
  // Qui la tient : ma pile fait foi avant la carte ; puis ce que le serveur a
  // écrit ; puis, tant qu'il ne sert pas `tenue_par`, une épingle protégée —
  // c'est la même attribution ouverte, lue par l'autre bout.
  let tenuePar: string | null = moi && maFile.has(carte.id) ? moi : (carte.tenue_par ?? null);
  if (tenuePar === null && carte.epingle_protegee === true && carte.epinglee_par) {
    tenuePar = carte.epinglee_par;
  }
  if (!par && !tenuePar) return AUCUNE_RESERVATION;
  const tenue = parMoi || carte.epingle_protegee === true || (par !== null && tenuePar === par);
  const unAutreLaTient = tenuePar !== null && tenuePar !== moi;
  return {
    par,
    parMoi,
    tenue,
    tenuePar,
    fermee: !estSuperviseur && unAutreLaTient,
  };
}

/** Le cadenas d'une ligne ou d'une carte, en primitives (les composants sont
 *  memoïsés sur elles). */
export interface Cadenas {
  status: LockStatus;
  blocked: boolean;
  name: string | null;
  avatarUrl: string | null;
}

/**
 * 🔒 LE CADENAS — qui est dans la discussion, et si je peux y entrer.
 *
 * Deux sources disent la même chose et se lisent dans cet ordre : le verrou
 * de l'onglet (Convex, `viewLock`) — il l'a OUVERTE — puis l'attribution de
 * la pile (`reservation().tenuePar`) — elle est dans SA file. L'écran ne
 * fait pas deux cadenas : « qui y a dans la conversation » (Kayla, 11-09)
 * est une seule question, et la porte une seule porte. Le verrou passe
 * devant parce qu'il porte le pseudo et l'avatar de Convex ; l'attribution
 * n'a qu'un identifiant, que `noms` traduit quand elle le peut.
 *
 * Ce que je tiens moi-même par attribution reste `free` : ma file est déjà
 * la mienne, la ligne n'a rien de plus à dire.
 */
export function cadenas(
  verrou: LockView,
  r: Reservation,
  moi: string | null,
  estSuperviseur: boolean,
  noms: ReadonlyMap<string, string>,
): Cadenas {
  if (verrou.status !== "free") {
    return {
      status: verrou.status,
      blocked: verrou.blocked,
      name: verrou.lock?.lockedByName ?? null,
      avatarUrl: verrou.lock?.lockedByAvatarUrl ?? null,
    };
  }
  if (r.tenuePar !== null && r.tenuePar !== moi) {
    return {
      status: "other",
      blocked: !estSuperviseur,
      name: noms.get(r.tenuePar) ?? null,
      avatarUrl: null,
    };
  }
  return { status: "free", blocked: false, name: null, avatarUrl: null };
}

/** La clé i18n et le mot par défaut. Signal inconnu ⇒ son nom, tel quel. */
export function motDuSignal(signal: string): { cle: string; defaut: string } {
  return {
    cle: `panels.conversations.pile.signal.${signal}`,
    defaut: MOT_DU_SIGNAL[signal] ?? signal,
  };
}

/**
 * 🎨 Le TON du `Badge` d'un signal (nouveau thème, 01-11-2026). Les quatre
 * teintes d'avant (vert / bleu / rouge / rose) disent la même chose en couleurs
 * de SENS, les seules que Kayla garde : le vert parle d'argent (payé, prix,
 * offre en l'air), le rouge de ce qui va mal (« te prend pour un bot »), l'ambre
 * de ce qui chauffe (« répond vite »), le gris du reste — un nouveau fan n'est
 * pas « à toi », il n'a pas la marque. Signal inconnu ⇒ gris, comme avant.
 */
const TON_DU_SIGNAL: Record<string, TonBadge> = {
  paye: "vert",
  demande_de_prix: "vert",
  prix_annonce: "vert",
  relance_apres_offre: "vert",
  offre_en_attente: "vert",
  nouveau: "gris",
  accusation_bot: "rouge",
  attention_haute: "ambre",
};

export function tonDuSignal(signal: string): TonBadge {
  return TON_DU_SIGNAL[signal] ?? "gris";
}

/* ────────────────────────────────────────────────────────────────────────── */
/* LE MOMENT — ce qui a coupé Cyrano, et que personne n'a repris               */
/* ────────────────────────────────────────────────────────────────────────── */
/*
 * Kayla, 12-09-2026 : « quand il y a un flow qui est terminé, il y aurait une
 * pastille "Flow terminé", par exemple, ou "Ultimatum" ». La carte de la
 * grille clignote depuis le 21-08 sur `moment_a_reprendre` ; la LIGNE de la
 * pile, elle, ne le montrait nulle part — un flow pouvait finir sur trente
 * discussions sans qu'une seule ligne bouge.
 *
 * Le mot suit la discipline du signal : court, en français, la couleur de
 * `MOMENT_COLOR` (la même table que la carte et La Régie — pas de cinquième
 * bleu). ⛔ Un moment que cette table ne connaît pas se rend sous sa clé, en
 * gris : `asMomentAReprendre` (atoms.ts) l'a déjà filtré, mais le jour où il
 * en laisse passer un de plus, la ligne le montre au lieu de le taire.
 */

const MOT_DU_MOMENT: Record<MomentAReprendre, string> = {
  flow_termine: "flow terminé",
  ultimatum: "ultimatum",
  // Le nom que Kayla lui donne (18-08) — pas la clé du geste.
  relance_du_jeu: "ultimatum du jeu",
  lot_a_lancer: "lot à lancer",
  annonce_payante: "payant",
  question_de_prix: "prix",
  demande_de_contenu: "contenu",
  accusation_bot: "bot",
};

/** ⛔ Deux pastilles pour un seul fait. Ces deux moments sont posés par les
 *  MÊMES détecteurs que les signaux de la file (`passer_la_main.demande_les_
 *  prix`, `.ne_te_croit_plus`) : quand la ligne porte déjà « prix » ou « bot »
 *  par son signal, la pastille du moment redirait le même mot dans la même
 *  couleur, collée à côté. On ne la pose alors pas. */
const MOMENT_DEJA_DIT_PAR_LE_SIGNAL: Partial<Record<MomentAReprendre, string>> = {
  question_de_prix: "demande_de_prix",
  accusation_bot: "accusation_bot",
  // 2.0 s'est coupé sur « c'est payant » : la file en fait « prix annoncé »
  // (même `motif_pause_le`), la ligne n'a pas à l'écrire deux fois.
  annonce_payante: "prix_annonce",
};

/** 🎨 Le ton du moment : l'argent en vert, le bot en rouge, et tout ce qui
 *  veut qu'un humain reprenne la main (un flow fini, un ultimatum, le jeu) en
 *  ambre — « attention ». L'ancien bleu et l'ancien rose n'étaient pas des
 *  sens, seulement deux teintes de plus. */
const TON_DU_MOMENT: Record<MomentAReprendre, TonBadge> = {
  flow_termine: "ambre",
  ultimatum: "ambre",
  relance_du_jeu: "ambre",
  lot_a_lancer: "ambre",
  annonce_payante: "vert",
  question_de_prix: "vert",
  demande_de_contenu: "vert",
  accusation_bot: "rouge",
};

/** La pastille du moment sur une ligne, ou `null` : pas de moment, ou le
 *  signal de la ligne le dit déjà. La couleur est le `ton` d'un `Badge`. */
export function pastilleDuMoment(
  moment: MomentAReprendre | null | undefined,
  signal: string,
): { cle: string; defaut: string; ton: TonBadge } | null {
  if (!moment) return null;
  if (MOMENT_DEJA_DIT_PAR_LE_SIGNAL[moment] === signal) return null;
  return {
    cle: `panels.conversations.pile.moment.${moment}`,
    defaut: MOT_DU_MOMENT[moment] ?? moment,
    ton: TON_DU_MOMENT[moment] ?? "gris",
  };
}

/** 💶 Le montant d'une offre en l'air, tel que la carte le lit. `formatRevenue`
 *  ne connaît que EUR/USD ; une autre devise s'écrit telle quelle plutôt que
 *  d'être déguisée en euros. */
export function formatOffre(montantCents: number, devise: string): string {
  const d = (devise || "").toUpperCase();
  if (d === "EUR" || d === "USD") return formatRevenue(montantCents, d);
  return `${(montantCents / 100).toFixed(2)} ${d}`.trim();
}

export interface MontantEnJeu {
  montant_cents: number;
  devise: string;
}

/**
 * L'argent en jeu sur la ligne : l'offre en l'air (`offre_en_attente`, servie
 * par la liste) — c'est elle qu'il faut encaisser. Rien d'autre.
 *
 * Ce qu'il a DÉJÀ dépensé (`revenue_cents`) n'est pas « en jeu » : il se lit
 * tel quel sur la pastille de l'avatar (Kayla, 09-09 : « sur la pastille
 * direct »). ⚠️ Le montant du DERNIER paiement n'est servi ni par la pile ni
 * par la carte ; on ne le fabrique pas.
 */
export function montantEnJeu(_item: ItemDePile, carte: MockConversation): MontantEnJeu | null {
  const o = carte.offre_en_attente;
  if (o && o.montant_cents > 0) return { montant_cents: o.montant_cents, devise: o.devise };
  return null;
}

/**
 * Depuis quand le fan attend — l'instant (epoch ms) d'où part le « il y a ».
 *
 * Le dernier message du fan quand c'est lui qui a parlé en dernier ; sinon la
 * date du signal telle que le serveur l'a posée (un paiement, une offre) —
 * c'est celle que sa phrase compte aussi. `null` si rien n'est datable.
 */
export function depuisQuandLeFanAttend(item: ItemDePile, carte: MockConversation): number | null {
  if (carte.last_author === "fan" && carte.last_message_at > 0) return carte.last_message_at;
  const le = Date.parse(item.signal_le);
  return Number.isFinite(le) ? le : null;
}

/**
 * L'item d'une carte servie par `sort=signaux` — pour la vue en lignes d'un
 * rôle qui n'a PAS de pile (l'admin regarde TOUTES les discussions, avec qui
 * les tient). Le signal, sa date, le rang et la phrase sont ceux que le
 * serveur a posés sur la carte ; sans signal, l'item en porte un vide et la
 * ligne ne dessine pas de mot — jamais un « attend » inventé.
 */
export function itemDeLaCarte(carte: MockConversation): ItemDePile {
  return {
    conversation_id: carte.id,
    creator_id: carte.creator_id,
    fan: { username: carte.fan_name, first_name: null },
    signal: carte.signal ?? "",
    signal_le: carte.signal_le ?? "",
    priorite_rang: typeof carte.priorite_rang === "number" ? carte.priorite_rang : 8,
    priorite_raison: carte.priorite_raison ?? "",
    attribuee_le: "",
    a_repondre_avant: null,
    epinglee: false,
  };
}

/** La tête de pile indexée par conversation — pour retrouver l'ITEM d'une carte
 *  au rendu sans parcourir la pile à chaque ligne. */
export function itemsParId(tete: readonly ItemDePile[]): Map<string, ItemDePile> {
  const m = new Map<string, ItemDePile>();
  for (const item of tete) if (!m.has(item.conversation_id)) m.set(item.conversation_id, item);
  return m;
}
